import { ReactElement, useCallback } from 'react';
import { InstantSearch } from 'react-instantsearch-hooks';

import { useTypesense } from '../../hooks/useTypesense';
import { InputAutocomplete, InputProps } from './InputAutocomplete';

export type AirportInputProps = InputProps & {
  displayValue?: 'name' | 'id';
  isMulti?: boolean;
  label?: ReactElement | string;
  hitValueKey?: string;
  includeAllOption?: boolean;
  extraOptions?: { label: string; value: string }[];
  isHighlighted?: boolean;
  isPortaled?: boolean;
};

export const AirportInput = ({
  displayValue = 'name',
  isMulti = false,
  hitValueKey = 'airport_id',
  includeAllOption = false,
  extraOptions = [],
  ...props
}: AirportInputProps) => {
  const { searchClient } = useTypesense({ additionalSearchParameters: { query_by: 'airport_id,airport_name' } });

  const getDisplayValue = useCallback(
    (hit: any) => {
      if (displayValue === 'id') return hit.airport_id;
      return `${hit.airport_name} (${hit.airport_id})`;
    },
    [displayValue],
  );

  const hitMap = useCallback(
    (hits: any[]) => {
      const options = hits.map((hit) => ({ label: getDisplayValue(hit), value: hit?.[hitValueKey] }));

      // Insert extra options at the beginning of the list
      if (extraOptions.length) options.unshift(...extraOptions);

      // Insert "Any" option at the beginning of the list
      if (includeAllOption) options.unshift({ label: 'Any', value: 'ALL' });

      return options;
    },
    [extraOptions, includeAllOption, getDisplayValue, hitValueKey],
  );

  if (!searchClient) return null;

  return (
    <InstantSearch searchClient={searchClient} indexName="airports">
      <InputAutocomplete showDropdownIndicator={false} isMulti={isMulti} hitMap={hitMap} {...props} />
    </InstantSearch>
  );
};
